// @ts-check
// Peças da paleta do editor de blocos e regras puras de posição (spec/email.md seções 3.4 e 19.2): o que cada peça
// cria, o rótulo e o ícone de cada tipo, o resumo de um bloco na lista do celular e para onde "mover para cima" e
// "mover para baixo" levam um bloco (atravessando seções quando ele está na ponta da coluna).
import { createEmailBlock, createEmailSection } from '../../../../../shared/email-design/email-document.js';
import { htmlToPlainText } from '../../../../../shared/email-design/html-to-text.js';

/**
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailBlock} EmailBlock
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailSection} EmailSection
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailSectionLayout} EmailSectionLayout
 * @typedef {import('../../../../../shared/email-design/document-operations.js').BlockTarget} BlockTarget
 * @typedef {'SECTION_1' | 'SECTION_2' | 'SECTION_3' | 'TEXT' | 'IMAGE' | 'LOGO' | 'BUTTON' | 'DIVIDER' | 'SPACER' | 'HTML'} PaletteItemKey
 * @typedef {{ key: PaletteItemKey, icon: string, kind: 'SECTION' | 'BLOCK', layout?: EmailSectionLayout }} PaletteItem
 * @typedef {{ code: string, blockId?: string }} CompileProblem
 */

/** @type {ReadonlyArray<PaletteItem>} */
export const PALETTE_ITEMS = Object.freeze([
  { key: 'SECTION_1', icon: 'IconLayoutRows', kind: 'SECTION', layout: '100' },
  { key: 'SECTION_2', icon: 'IconColumns', kind: 'SECTION', layout: '50-50' },
  { key: 'SECTION_3', icon: 'IconLayoutGrid', kind: 'SECTION', layout: '33-33-33' },
  { key: 'TEXT', icon: 'IconTypography', kind: 'BLOCK' },
  { key: 'IMAGE', icon: 'IconPhoto', kind: 'BLOCK' },
  { key: 'LOGO', icon: 'IconBuildingCommunity', kind: 'BLOCK' },
  { key: 'BUTTON', icon: 'IconClick', kind: 'BLOCK' },
  { key: 'DIVIDER', icon: 'IconSeparatorHorizontal', kind: 'BLOCK' },
  { key: 'SPACER', icon: 'IconSpacingVertical', kind: 'BLOCK' },
  { key: 'HTML', icon: 'IconCode', kind: 'BLOCK' },
]);

export const BLOCK_PALETTE_ITEMS = PALETTE_ITEMS.filter((item) => item.kind === 'BLOCK');
export const SECTION_PALETTE_ITEMS = PALETTE_ITEMS.filter((item) => item.kind === 'SECTION');

/** @type {Readonly<Record<EmailBlock['type'], string>>} */
export const BLOCK_ICONS = Object.freeze({
  TEXT: 'IconTypography', IMAGE: 'IconPhoto', BUTTON: 'IconClick', DIVIDER: 'IconSeparatorHorizontal', SPACER: 'IconSpacingVertical', HTML: 'IconCode',
});

export const SECTION_LAYOUT_KEYS = /** @type {ReadonlyArray<EmailSectionLayout>} */ (['100', '50-50', '33-67', '67-33', '33-33-33']);

/** @param {string} key */
export const findPaletteItem = (key) => PALETTE_ITEMS.find((item) => item.key === key) ?? null;

/**
 * Chave de rótulo do bloco (o logo é uma imagem com a fonte do logo).
 * @param {EmailBlock} block
 * @returns {PaletteItemKey}
 */
export function blockPaletteKey(block) {
  if (block.type === 'IMAGE' && block.source?.kind === 'INSTITUTION_LOGO') return 'LOGO';
  return block.type;
}

/**
 * Bloco novo de uma peça da paleta.
 * @param {PaletteItemKey} key @param {() => string} createId
 * @returns {EmailBlock | null}
 */
export function createBlockFromPalette(key, createId) {
  if (key === 'LOGO') return createEmailBlock('IMAGE', createId, { institutionLogo: true });
  if (key === 'TEXT' || key === 'IMAGE' || key === 'BUTTON' || key === 'DIVIDER' || key === 'SPACER' || key === 'HTML') return createEmailBlock(key, createId);
  return null;
}

/** Seção nova vazia de uma peça de seção. @param {PaletteItemKey} key @param {() => string} createId @returns {EmailSection | null} */
export function createSectionFromPalette(key, createId) {
  const item = findPaletteItem(key);
  return item?.kind === 'SECTION' && item.layout ? createEmailSection(item.layout, createId) : null;
}

/**
 * Texto curto do bloco para a lista do celular e os anúncios.
 * @param {EmailBlock} block
 */
export function blockSummary(block) {
  switch (block.type) {
    case 'TEXT':
      return htmlToPlainText(block.html).replace(/\s+/g, ' ').trim().slice(0, 80);
    case 'BUTTON':
      return block.label.trim();
    case 'IMAGE':
      return block.alt.trim();
    case 'HTML':
      return htmlToPlainText(block.html).replace(/\s+/g, ' ').trim().slice(0, 80);
    default:
      return '';
  }
}

/** Posição de cada bloco na ordem de leitura (1 em diante), para os anúncios. @param {EmailDocument} document */
export function readingOrder(document) {
  /** @type {Map<string, number>} */
  const order = new Map();
  let position = 0;
  for (const section of document.sections ?? []) {
    for (const column of section.columns) {
      for (const block of column.blocks) {
        position += 1;
        order.set(block.id, position);
      }
    }
  }
  return order;
}

/**
 * Destino de "mover para cima" ou "mover para baixo": dentro da coluna, ou para o fim (cima) ou o começo (baixo) da
 * coluna de mesmo índice da seção vizinha. Nulo na ponta do e-mail. O índice segue moveBlock (antes de tirar o bloco).
 * @param {EmailDocument} document @param {BlockTarget} from @param {-1 | 1} direction
 * @returns {BlockTarget | null}
 */
export function adjacentBlockTarget(document, from, direction) {
  const sections = document.sections ?? [];
  const sectionIndex = sections.findIndex((section) => section.id === from.sectionId);
  const section = sections[sectionIndex];
  if (!section) return null;
  const column = section.columns[from.columnIndex];
  if (direction === -1 && from.index > 0) return { ...from, index: from.index - 1 };
  if (direction === 1 && from.index < column.blocks.length - 1) return { ...from, index: from.index + 2 };
  const neighbor = sections[sectionIndex + direction];
  if (!neighbor) return null;
  const columnIndex = Math.min(from.columnIndex, neighbor.columns.length - 1);
  return { sectionId: neighbor.id, columnIndex, index: direction === -1 ? neighbor.columns[columnIndex].blocks.length : 0 };
}

/**
 * Erros e avisos da compilação por bloco.
 * @param {{ errors: CompileProblem[], warnings: CompileProblem[] }} problems
 */
export function groupProblemsByBlock(problems) {
  /** @type {Map<string, { errors: string[], warnings: string[] }>} */
  const byBlock = new Map();
  const entry = (/** @type {string} */ blockId) => {
    if (!byBlock.has(blockId)) byBlock.set(blockId, { errors: [], warnings: [] });
    return /** @type {{ errors: string[], warnings: string[] }} */ (byBlock.get(blockId));
  };
  for (const problem of problems.errors) if (problem.blockId) entry(problem.blockId).errors.push(problem.code);
  for (const problem of problems.warnings) if (problem.blockId) entry(problem.blockId).warnings.push(problem.code);
  return byBlock;
}
